/** @jsxImportSource @emotion/react */
import { Box, Button, LinearProgress, Typography } from '@mui/material'
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form'

import { DataUploadFormData } from '@app/modules/Home/Data/types'
import { OGR_ACCEPT_ATTR } from '@app/modules/Home/Data/constants/ogr.formats'
import { IconOption } from '@app/modules/Home/Data/hooks/useIconOptions'
import DatasetFormFields from './DatasetFormFields'
import FilePickerField from './FilePickerField'
import { formOuter } from './DataUpload.styles'

interface UploadFormStepProps {
  inputRef: React.RefObject<HTMLInputElement | null>
  register: UseFormRegister<DataUploadFormData>
  control: Control<DataUploadFormData>
  errors: FieldErrors<DataUploadFormData>
  selectedFiles: File[]
  geometryType: string | undefined
  iconOptions: IconOption[]
  onGeometryChange: (field: { onChange: (v: string | undefined) => void }, value: string) => void
  onSubmit: React.FormEventHandler<HTMLFormElement>
  uploading: boolean
  extracting: boolean
  progress: number
  status: string
  errorMessage: string | null | undefined
  isSubmitting: boolean
}

const UploadFormStep = ({
  inputRef,
  register,
  control,
  errors,
  selectedFiles,
  geometryType,
  iconOptions,
  onGeometryChange,
  onSubmit,
  uploading,
  extracting,
  progress,
  status,
  errorMessage,
  isSubmitting,
}: UploadFormStepProps) => {
  const { ref: fileRef, ...fileRegisterProps } = register('file')

  return (
    <Box component="form" css={formOuter} onSubmit={onSubmit}>
      <FilePickerField
        inputRef={inputRef}
        rhfRef={fileRef}
        rhfInputProps={fileRegisterProps}
        selectedFiles={selectedFiles}
        onFilesChange={() => {}}
        error={errors.file?.message as string | undefined}
        accept={OGR_ACCEPT_ATTR}
      />

      <DatasetFormFields
        control={control}
        register={register}
        errors={errors}
        iconOptions={iconOptions}
        geometryType={geometryType}
        onGeometryChange={onGeometryChange}
      />

      <Button
        type="submit"
        variant="contained"
        disabled={uploading || isSubmitting}
        sx={{ alignSelf: 'flex-start' }}
      >
        {uploading ? 'Uploading…' : extracting ? 'Extracting attributes…' : 'Upload'}
      </Button>

      {uploading && <LinearProgress variant="determinate" value={progress} />}
      {extracting && !uploading && <LinearProgress variant="indeterminate" />}
      {status === 'error' && (
        <Typography variant="body2" color="error.main">
          {errorMessage}
        </Typography>
      )}
    </Box>
  )
}

export default UploadFormStep
